import { Check, Star, Mic } from 'lucide-react';
import type { RundownClaimFull } from '@news-assist/shared';

interface ClaimItemProps {
  claim: RundownClaimFull;
  onToggleIncluded: (included: boolean) => void;
  onToggleStarred: (starred: boolean) => void;
  onToggleClip: (isClip: boolean) => void;
}

export function ClaimItem({
  claim,
  onToggleIncluded,
  onToggleStarred,
  onToggleClip,
}: ClaimItemProps) {
  return (
    <div
      className={`
        p-3 rounded-lg border transition-colors
        ${claim.included
          ? 'bg-white border-gray-200'
          : 'bg-gray-50 border-gray-100 opacity-60'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* Include checkbox */}
        <button
          onClick={() => onToggleIncluded(!claim.included)}
          className={`
            flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
            ${claim.included
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'border-gray-300 hover:border-gray-400'
            }
          `}
        >
          {claim.included && <Check className="w-3 h-3" />}
        </button>

        {/* Claim content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${claim.included ? 'text-gray-900' : 'text-gray-500'}`}>
            {claim.claim.content}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-400">
              {claim.claim.claimType}
            </span>
          </div>
        </div>

        {/* Star toggle */}
        <button
          onClick={() => onToggleStarred(!claim.starred)}
          disabled={!claim.included}
          className={`
            flex-shrink-0 p-1.5 rounded transition-colors
            ${claim.starred
              ? 'text-amber-500 bg-amber-50'
              : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50'
            }
            ${!claim.included && 'opacity-50 cursor-not-allowed'}
          `}
          title={claim.starred ? 'Remove star' : 'Star this claim'}
        >
          <Star className={`w-4 h-4 ${claim.starred ? 'fill-current' : ''}`} />
        </button>

        {/* Clip toggle */}
        <button
          onClick={() => onToggleClip(!claim.isClip)}
          disabled={!claim.included}
          className={`
            flex-shrink-0 p-1.5 rounded transition-colors
            ${claim.isClip
              ? 'text-purple-600 bg-purple-50'
              : 'text-gray-300 hover:text-purple-500 hover:bg-purple-50'
            }
            ${!claim.included && 'opacity-50 cursor-not-allowed'}
          `}
          title={claim.isClip ? 'Remove clip marker' : 'Mark as clip/soundbite'}
        >
          <Mic className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
